import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  downloadPost,
  request,
  type MatchCandidatesResponse,
  type MatchListResponse,
  type RedcapAnalyticsResponse,
  type RedcapBoardResponse,
  type RedcapDictionaryResponse,
  type RedcapExportPlanResponse,
  type RedcapMapResponse,
  type RedcapSetupResponse,
  type RedcapSuggestResponse,
  type RedcapChaseResult,
  type RedcapCompleteness,
  type RedcapExtractResult,
  type RedcapImportResult,
  type RedcapKeptFormsResult,
  type RedcapProjectResult,
  type RedcapRecordsResponse,
  type RedcapRuleKey,
  type RedcapRuleResult,
  type RedcapRecordRows,
  type RedcapSummaries,
  type RedcapWeekPage,
  type RedcapWeeks,
} from './client';

/**
 * REDCap, both ways it reaches this dashboard.
 *
 * The weekly completeness extract: a Monday CSV of counts per event and
 * instrument — how many forms are expected, complete and incomplete — never
 * a participant. The API refuses any column outside the extract's eight, keeps
 * each week, and opens a chase when a safety instrument stays incomplete two
 * Mondays running. The intake workflow (v1.0) is why it stops at counts:
 * REDCap is the only system that may hold clinical content.
 *
 * The PHI-Match import: a de-identified record export — a row per record per
 * event in a longitudinal project — refused whole if it carries an identifier
 * column. With the data dictionary on file it keeps only the forms chosen,
 * and never a field whose answers name a placebo arm.
 *
 * The setup is the lab's (043): every study has its own REDCap project, and
 * every project is built the same, so the dictionary, the forms kept and the
 * export map are kept once, under /redcap/setup, and every study reads them.
 *
 *   GET    /redcap/completeness                             studies:read
 *   GET    /redcap/completeness/{slug}                      studies:read
 *   PUT    /redcap/completeness/{slug}/project              redcap:import
 *   DELETE /redcap/completeness/{slug}/project              redcap:import
 *   PUT    /redcap/completeness/{slug}/rules/{instrument}   redcap:import
 *   GET    /redcap/completeness/{slug}/week?asOf=           studies:read    the week on one page (044)
 *   GET    /redcap/weeks                                    studies:read    every study's newest week
 *   POST   /redcap/completeness/{slug}/extracts?asOf=       redcap:import   text/csv — counts file, or REDCap's status export
 *   POST   /redcap/completeness/{slug}/weeks/{asOf}/{kind}  redcap:import   text/csv — the calendar or queries export beside a week
 *   PATCH  /redcap/chases/{id}                              redcap:import
 *   POST   /matching/redcap/import?study=                   redcap:import   text/csv
 *   GET    /matching/redcap                                 submissions:read
 *
 * docs/api-contract-redcap.md is the record of the contract.
 */

/** The extract's columns, in template order. The API accepts exactly these. */
export const EXTRACT_COLUMNS = [
  'studySlug',
  'eventName',
  'instrument',
  'nExpected',
  'nComplete',
  'nIncomplete',
  'dictVersion',
  'asOf',
] as const;

export const RULE_OPTIONS: ReadonlyArray<{ key: RedcapRuleKey; label: string; blurb: string }> = [
  { key: 'chase', label: 'Chase', blurb: 'Incomplete on two extracts running opens a chase.' },
  { key: 'held', label: 'Held', blurb: 'Chases like the rest, but only once its window has closed.' },
  { key: 'tint', label: 'Highlight', blurb: 'Incompletes are highlighted and never chased.' },
  { key: 'none', label: 'No clock', blurb: 'Counts only.' },
];

export const ruleLabel = (key: RedcapRuleKey): string =>
  RULE_OPTIONS.find((r) => r.key === key)?.label ?? key;

const COMPLETENESS = ['redcap-completeness'] as const;

export const useRedcapSummaries = () =>
  useQuery({
    queryKey: COMPLETENESS,
    queryFn: () => request<RedcapSummaries>('/redcap/completeness'),
  });

export const useCompleteness = (slug: string | null) =>
  useQuery({
    queryKey: [...COMPLETENESS, slug],
    queryFn: () => request<RedcapCompleteness>(`/redcap/completeness/${encodeURIComponent(slug!)}`),
    enabled: slug !== null,
  });

/** Every write here can change a study's week pages and the Dashboard's list both. */
function useCompletenessMutation<V, R>(fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMPLETENESS });
      void qc.invalidateQueries({ queryKey: ['redcap-week'] });
      void qc.invalidateQueries({ queryKey: ['redcap-weeks'] });
      void qc.invalidateQueries({ queryKey: ['redcap-board'] });
    },
  });
}

/**
 * Upload a week: the eight-column counts file, which names its own Monday, or
 * REDCap's status export, for which `asOf` says which Monday it is for.
 */
export const useUploadExtract = () =>
  useCompletenessMutation(({ slug, csv, asOf }: { slug: string; csv: string; asOf?: string }) =>
    request<RedcapExtractResult>(`/redcap/completeness/${encodeURIComponent(slug)}/extracts`, {
      method: 'POST',
      query: { asOf },
      body: csv,
      rawBody: true,
      contentType: 'text/csv',
    }),
  );

export type WeekFileKind = 'visits' | 'queries';

export interface WeekFileResult {
  snapshotId: string;
  asOf: string;
  kind: WeekFileKind;
  rows: number;
  replaced: boolean;
  droppedColumns: string[];
  byStatus: Record<string, number>;
}

/** The calendar or the data-quality export, beside a week whose status file is already in. */
export const useUploadWeekFile = () =>
  useCompletenessMutation(({ slug, asOf, kind, csv }: { slug: string; asOf: string; kind: WeekFileKind; csv: string }) =>
    request<WeekFileResult>(
      `/redcap/completeness/${encodeURIComponent(slug)}/weeks/${encodeURIComponent(asOf)}/${kind}`,
      { method: 'POST', body: csv, rawBody: true, contentType: 'text/csv' },
    ),
  );

/** One week on one page. `asOf` null is the newest; the previous page stays while the next loads. */
export const useRedcapWeek = (slug: string | null, asOf: string | null) =>
  useQuery({
    queryKey: ['redcap-week', slug, asOf],
    queryFn: () =>
      request<RedcapWeekPage>(`/redcap/completeness/${encodeURIComponent(slug!)}/week`, {
        query: { asOf: asOf ?? undefined },
      }),
    enabled: slug !== null,
    placeholderData: keepPreviousData,
  });

/** Every linked study's newest week — the Dashboard card and the step rail read this. */
export const useRedcapWeeks = (enabled = true) =>
  useQuery({
    queryKey: ['redcap-weeks'],
    queryFn: () => request<RedcapWeeks>('/redcap/weeks'),
    enabled,
  });

/** Link a study to its REDCap project. `eventName` undefined keeps it; null clears it. */
export function useSetRedcapProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, pid, eventName }: { slug: string; pid: number; eventName?: string | null }) =>
      request<RedcapProjectResult>(`/redcap/completeness/${encodeURIComponent(slug)}/project`, {
        method: 'PUT',
        body: eventName === undefined ? { pid } : { pid, eventName },
      }),
    onSuccess: (_d, { slug }) => invalidateStudy(qc, slug),
  });
}

export function useRemoveRedcapProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      request<{ ok: boolean }>(`/redcap/completeness/${encodeURIComponent(slug)}/project`, {
        method: 'DELETE',
      }),
    onSuccess: (_d, slug) => invalidateStudy(qc, slug),
  });
}

export const useSetInstrumentRule = () =>
  useCompletenessMutation(
    ({
      slug,
      instrument,
      rule,
      windowClosed,
      recordRows,
    }: {
      slug: string;
      instrument: string;
      rule: RedcapRuleKey;
      windowClosed: boolean;
      /** Whether the week lists this form's late records by record ID or only counts them; absent keeps it. */
      recordRows?: RedcapRecordRows;
    }) =>
      request<RedcapRuleResult>(
        `/redcap/completeness/${encodeURIComponent(slug)}/rules/${encodeURIComponent(instrument)}`,
        { method: 'PUT', body: recordRows ? { rule, windowClosed, recordRows } : { rule, windowClosed } },
      ),
  );

export const RECORD_ROWS_OPTIONS: ReadonlyArray<{ key: RedcapRecordRows; label: string; blurb: string }> = [
  { key: 'list', label: 'Listed', blurb: 'The week names each record behind on this form.' },
  { key: 'count', label: 'Counted', blurb: 'The week says how many records are behind, never which.' },
];

export const useUpdateChase = () =>
  useCompletenessMutation(
    ({ id, ...body }: { id: string; status?: 'open' | 'done'; ownerId?: string | null }) =>
      request<RedcapChaseResult>(`/redcap/chases/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
      }),
  );

/** The PHI-Match import. Sent as CSV text, not multipart. */
export function useRedcapImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, study }: { csv: string; study: string }) =>
      request<RedcapImportResult>('/matching/redcap/import', {
        method: 'POST',
        query: { study },
        body: csv,
        rawBody: true,
        contentType: 'text/csv',
      }),
    onSuccess: (_d, { study }) => {
      void qc.invalidateQueries({ queryKey: ['redcap-records'] });
      void qc.invalidateQueries({ queryKey: ['redcap-analytics', study] });
      void qc.invalidateQueries({ queryKey: ['redcap-board', study] });
    },
  });
}

export interface RecordFilters {
  study: string;
  matched?: 'yes' | 'no';
  /** One record, by its REDCap record ID — the week's "Open record". */
  recordId?: string;
  limit: number;
  offset: number;
}

export const useRedcapRecords = (filters: RecordFilters, enabled = true) =>
  useQuery({
    queryKey: ['redcap-records', filters],
    queryFn: () =>
      request<RedcapRecordsResponse>('/matching/redcap', {
        query: {
          study: filters.study,
          matched: filters.matched,
          recordId: filters.recordId,
          limit: filters.limit,
          offset: filters.offset,
        },
      }),
    enabled,
  });

/**
 * The header of the REDCap report a study builds once: the record ID, the
 * event, the repeat columns and every form's status. Downloaded to check a
 * report against, not to fill in — the rows come from REDCap.
 */
export function statusReportTemplate(recordIdField: string | null, forms: readonly string[]): string {
  const cols = [recordIdField ?? 'record_id', 'redcap_event_name', 'redcap_repeat_instrument', 'redcap_repeat_instance'];
  return `${[...cols, ...forms.map((f) => `${f}_complete`)].join(',')}\n`;
}

/** The calendar export's columns, as the reader takes them. */
export const VISITS_TEMPLATE = 'date,record,event,status\n2026-10-05,1001,v1_arm_1,Scheduled\n';

/** The Resolve Issues export's columns, as the reader takes them. */
export const QUERIES_TEMPLATE = 'record,event,field,rule,status,date_opened,date_closed,assigned_user\n1001,v1_arm_1,bdi_total,Missing value,Open,2026-09-28,,coordinator\n';


/* ── A study's REDCap setup: dictionary, export map, exports, analytics ─── */

const enc = encodeURIComponent;

/** Everything one study's REDCap setup feeds. */
function invalidateStudy(qc: ReturnType<typeof useQueryClient>, slug: string): void {
  void qc.invalidateQueries({ queryKey: COMPLETENESS });
  for (const key of ['redcap-dictionary', 'redcap-map', 'redcap-export', 'redcap-analytics', 'redcap-board']) {
    void qc.invalidateQueries({ queryKey: [key, slug] });
  }
}

/**
 * Everything the lab's setup feeds: a change to it reaches every study's
 * dictionary view, export, analytics, board and completeness at once.
 */
function invalidateSetup(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: COMPLETENESS });
  for (const key of ['redcap-setup', 'redcap-dictionary', 'redcap-map', 'redcap-export', 'redcap-analytics', 'redcap-board']) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

/** The lab's REDCap setup: the dictionary every study's project shares, and the forms kept. */
export const useRedcapSetup = () =>
  useQuery({
    queryKey: ['redcap-setup'],
    queryFn: () => request<RedcapSetupResponse>('/redcap/setup/'),
  });

/** A study's project, with the lab's dictionary beside it. */
export const useDictionary = (slug: string | null) =>
  useQuery({
    queryKey: ['redcap-dictionary', slug],
    queryFn: () => request<RedcapDictionaryResponse>(`/redcap/studies/${enc(slug!)}/dictionary`),
    enabled: slug !== null,
  });

/** Upload REDCap's data dictionary, for every study. The file name carries its date. */
export function useUploadDictionary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, name }: { csv: string; name: string }) =>
      request<RedcapSetupResponse>('/redcap/setup/dictionary', {
        method: 'PUT',
        query: { name },
        body: csv,
        rawBody: true,
        contentType: 'text/csv',
      }),
    onSuccess: () => invalidateSetup(qc),
  });
}

/** Choose which REDCap forms a record import keeps, for every study. */
export function useSetKeptForms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ forms }: { forms: string[] }) =>
      request<RedcapKeptFormsResult>('/redcap/setup/kept-forms', { method: 'PUT', body: { forms } }),
    onSuccess: () => invalidateSetup(qc),
  });
}

/** The export map every study uses. */
export const useExportMap = () =>
  useQuery({
    queryKey: ['redcap-map'],
    queryFn: () => request<RedcapMapResponse>('/redcap/setup/map'),
  });

export interface MapInputRow {
  variable: string;
  source: string;
  valueMap: Record<string, string> | null;
}

export function useSaveExportMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rows }: { rows: MapInputRow[] }) =>
      request<RedcapMapResponse>('/redcap/setup/map', { method: 'PUT', body: { rows } }),
    onSuccess: (data) => {
      qc.setQueryData(['redcap-map'], data);
      // Every study's export reads this map.
      void qc.invalidateQueries({ queryKey: ['redcap-export'] });
    },
  });
}

export const useSuggestMap = () =>
  useMutation({
    mutationFn: ({ forms }: { forms: string[] }) =>
      request<RedcapSuggestResponse>('/redcap/setup/map/suggest', {
        method: 'POST',
        body: { forms },
      }),
  });

export interface ExportFilterInput {
  form: 'intake' | 'referral';
  since: string | null;
  status: string | null;
  onlyNew: boolean;
}

export const useExportPlan = (slug: string | null, filter: ExportFilterInput, enabled = true) =>
  useQuery({
    queryKey: ['redcap-export', slug, filter],
    queryFn: () =>
      request<RedcapExportPlanResponse>(`/redcap/studies/${enc(slug!)}/export`, {
        query: {
          form: filter.form,
          since: filter.since ?? undefined,
          statuses: filter.status ?? undefined,
          onlyNew: String(filter.onlyNew),
        },
      }),
    enabled: slug !== null && enabled,
  });

/** Take the file. Records which sign-ups it held, so the next "new only" leaves them out. */
export function useDownloadExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, filter }: { slug: string; filter: ExportFilterInput }) =>
      downloadPost(
        `/redcap/studies/${enc(slug)}/export`,
        {
          form: filter.form,
          since: filter.since,
          statuses: filter.status ? [filter.status] : undefined,
          onlyNew: filter.onlyNew,
        },
        `redcap-${slug}-${filter.form}.csv`,
      ),
    onSuccess: (_name, { slug }) => {
      void qc.invalidateQueries({ queryKey: ['redcap-export', slug] });
      void qc.invalidateQueries({ queryKey: ['redcap-board', slug] });
    },
  });
}

/**
 * Counts over a study's imported REDCap rows. `event` narrows to one event
 * (null counts every event's rows); `form` to one form's fields.
 */
export const useRedcapAnalytics = (slug: string | null, filter: { event: string | null; form: string | null }) =>
  useQuery({
    queryKey: ['redcap-analytics', slug, filter.event, filter.form],
    queryFn: () =>
      request<RedcapAnalyticsResponse>(`/redcap/studies/${enc(slug!)}/analytics`, {
        query: { event: filter.event ?? undefined, form: filter.form ?? undefined },
      }),
    enabled: slug !== null,
    placeholderData: keepPreviousData,
  });

/** What a study's participant board shows of REDCap. */
export const useRedcapBoard = (slug: string | null, enabled = true) =>
  useQuery({
    queryKey: ['redcap-board', slug],
    queryFn: () => request<RedcapBoardResponse>(`/redcap/studies/${enc(slug!)}/board`),
    enabled: slug !== null && enabled,
  });

/* ── Matching: linking a de-identified record to a sign-up (PHI-Match) ──── */

/** Suggested sign-ups for one record. Needs PHI-Match "match"; fetched only when asked for. */
export const useMatchCandidates = (redcapId: string | null) =>
  useQuery({
    queryKey: ['redcap-candidates', redcapId],
    queryFn: () => request<MatchCandidatesResponse>('/matching/candidates', { query: { redcapId: redcapId! } }),
    enabled: redcapId !== null,
    staleTime: 0,
  });

/** One record's link. Reading it is a PHI access, logged — so only when someone opens it. */
export const useRecordMatch = (redcapId: string | null) =>
  useQuery({
    queryKey: ['redcap-match', redcapId],
    queryFn: () =>
      request<MatchListResponse>('/matching', { query: { redcapId: redcapId!, includeBroken: 'false' } }),
    enabled: redcapId !== null,
    staleTime: 0,
  });

function useMatchMutation<V>(fn: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['redcap-records'] });
      void qc.invalidateQueries({ queryKey: ['redcap-match'] });
      void qc.invalidateQueries({ queryKey: ['redcap-candidates'] });
    },
  });
}

export const MATCH_METHODS = [
  { key: 'inquiry_id', label: 'Inquiry ID quoted in the record' },
  { key: 'email', label: 'Same email (checked outside the CRM)' },
  { key: 'phone', label: 'Same phone (checked outside the CRM)' },
  { key: 'manual', label: 'Manual review' },
] as const;
export type MatchMethod = (typeof MATCH_METHODS)[number]['key'];

export const useCreateMatch = () =>
  useMatchMutation(
    (body: { submissionId: string; redcapId: string; method: MatchMethod; note?: string }) =>
      request<{ id: string }>('/matching', { method: 'POST', body }),
  );

export const useBreakMatch = () =>
  useMatchMutation(({ id, reason }: { id: string; reason: string }) =>
    request<{ ok: boolean }>(`/matching/${enc(id)}/unmatch`, { method: 'POST', body: { reason } }),
  );
